import { green, red } from '@material-ui/core/colors';
import { createMuiTheme, Theme } from '@material-ui/core/styles';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import createSpacing from '@material-ui/core/styles/createSpacing';
import { Theme as ThemeEnum } from 'enum';
type PaletteType = 'light' | 'dark' | undefined;

const breakpoints = createBreakpoints({});
const spacing = createSpacing(8);

// A custom theme for this app
const theme = (type: PaletteType): Theme => {
    const isDarkMode = type === ThemeEnum.Dark;
    return createMuiTheme({
        overrides: {
            MuiCssBaseline: {
                '@global': {
                    html: {
                        fontSize: 16,
                        [breakpoints.down('md')]: {
                            fontSize: 15.5,
                        },
                        [breakpoints.down('xs')]: {
                            fontSize: 15,
                        },
                    },
                    body: {
                        fontSize: '1rem',
                    },
                },
            },
            MuiButton: {
                root: {
                    borderRadius: 8,
                },
            },
            MuiCard: {
                root: {
                    borderRadius: 8,
                },
            },
            MuiPaper: {
                root: {
                    backgroundColor: isDarkMode ? 'rgb(44, 44, 44)' : 'rgb(255,255,255)',
                },
                rounded: {
                    borderRadius: 8,
                },
            },
            MuiIconButton: {
                sizeSmall: {
                    padding: spacing(0.75),
                },
            },
            MuiTab: {
                wrapper: {
                    fontWeight: 600,
                },
            },
            MuiCardHeader: {
                title: {
                    fontSize: '1.3rem',
                    fontWeight: 500,
                },
            },
            MuiDialog: {
                paper: {
                    minWidth: 500,
                    width: 500,
                    [breakpoints.down('xs')]: {
                        minWidth: 'unset',
                        width: '100%',
                    },
                },
            },
            MuiDialogTitle: {
                root: {
                    padding: spacing(2, 2.5),
                    fontSize: '1.3rem',
                    fontWeight: 500,
                },
            },
            MuiDialogContent: {
                root: {
                    padding: spacing(2.5),
                },
            },
            MuiDialogActions: {
                root: {
                    padding: spacing(2, 2.5),
                },
            },
            MuiOutlinedInput: {
                root: {
                    '& fieldset': {
                        borderRadius: 8,
                    },
                },
                adornedEnd: {
                    paddingRight: 0,
                },
            },
            MuiSelect: {
                select: {
                    '&:focus': {
                        borderRadius: 8,
                    },
                },
            },
        },
        palette: {
            type,
            primary: {
                main: type === 'light' ? '#0549AA' : '#3472D6',
                light: '#3472D6',
            },
            secondary: {
                main: '#f50057',
            },
            error: {
                main: red.A400,
            },
            success: { light: green[600], main: green[500], dark: green[400], contrastText: '#fff' },
            text: {
                secondary: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.7)',
            },
        },
        typography: {
            fontFamily: ['Inter', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
            h1: {
                fontSize: '2.275rem',
                fontWeight: 600,
            },
            h2: {
                fontSize: '1.625rem',
                fontWeight: 600,
            },
            h3: {
                fontSize: '1.375rem',
                fontWeight: 600,
            },
            h4: {
                fontSize: '1.25rem',
                fontWeight: 600,
            },
            h5: {
                fontSize: '1rem',
                fontWeight: 600,
            },
            h6: {
                fontSize: '0.875rem',
                fontWeight: 600,
            },
        },
        shadows: [
            'none',
            'rgba(0, 0, 0, 0.2) 0px 1px 2px 0px',
            'rgba(0, 0, 0, 0.25) 0px 1px 3px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 4px 1px',
            'rgba(0, 0, 0, 0.2) 0px 1px 5px 1px',
            'rgba(0, 0, 0, 0.2) 0px 1px 6px 2px',
            'rgba(0, 0, 0, 0.2) 0px 1px 7px 2px',
            'rgba(0, 0, 0, 0.2) 0px 1px 8px 2px',
            'rgba(0, 0, 0, 0.2) 0px 1px 9px 2px',
            'rgba(0, 0, 0, 0.2) 0px 1px 10px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 11px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 12px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 13px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 14px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 15px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 16px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 17px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 18px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 19px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 20px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 21px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 22px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 23px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 24px 0px',
            'rgba(0, 0, 0, 0.2) 0px 1px 25px 0px',
        ],
    });
};

export default theme;
